import { useState, useRef, useEffect } from "react";
import QuoteCarousel from "@/components/QuoteCarousel";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useLocation } from "wouter";
import { UserButton } from "@clerk/react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import {
  ArrowLeft, Printer, CheckCircle2, RefreshCw, AlertTriangle,
  DollarSign, Users, BarChart3, FileText, Building2,
} from "lucide-react";


// ── Helpers ──────────────────────────────────────────────────────────────────

function fmt(n: number | string | null | undefined, dec = 2): string {
  if (n == null) return "0.00";
  const v = typeof n === "string" ? parseFloat(n) : n;
  return v.toLocaleString("en-US", { minimumFractionDigits: dec, maximumFractionDigits: dec });
}

function fmtD(n: number | string | null | undefined): string {
  return "$" + fmt(n);
}

function shortDate(d: string | null | undefined): string {
  if (!d) return "—";
  return new Date(d).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
}

const currentYear = new Date().getFullYear();
const YEARS = Array.from({ length: 6 }, (_, i) => currentYear - i);

// ── Types ────────────────────────────────────────────────────────────────────

interface Data990 {
  taxYear: number;
  organizationName: string;
  ein: string;
  taxPeriodStart: string;
  taxPeriodEnd: string;
  partI: {
    line1_contributions: number;
    line2_programServiceRevenue: number;
    line3_membershipDues: number;
    line4_investmentIncome: number;
    line5_otherRevenue: number;
    line9_totalRevenue: number;
  };
  partII: {
    line10_grantsPaid: number;
    line13_managementExpenses: number;
    line14_fundraisingExpenses: number;
    line17_totalExpenses: number;
    line18_excessOrDeficit: number;
  };
  partIII: {
    line19_netAssetsBeginning: number;
    line20_otherChanges: number;
    line21_netAssetsEnd: number;
  };
  partIV: {
    programDescription: string;
    debtsClearedCount: number;
    beneficiaryCount: number;
    donorCount: number;
  };
  pools: { name: string; balance: number; status: string }[];
  activePools: number;
  totalNetAssets: number;
  donationCount: number;
  batchCount: number;
}

// ── Line Item Row ─────────────────────────────────────────────────────────────

function LineRow({
  lineNum,
  label,
  value,
  highlight = false,
  indent = false,
}: {
  lineNum: string;
  label: string;
  value: number;
  highlight?: boolean;
  indent?: boolean;
}) {
  return (
    <tr className={`border-b last:border-0 ${highlight ? "font-semibold" : ""}`}>
      <td className="py-2 px-3 text-xs font-mono text-muted-foreground w-16">{lineNum}</td>
      <td className={`py-2 px-3 text-sm ${indent ? "pl-8" : ""}`}>{label}</td>
      <td className="py-2 px-3 text-right text-sm font-mono">{fmtD(value)}</td>
    </tr>
  );
}

// ── Section Header ────────────────────────────────────────────────────────────

function SectionHeader({ children }: { children: React.ReactNode }) {
  return (
    <tr className="border-b">
      <td colSpan={3} className="py-2 px-3 text-xs font-bold uppercase tracking-wide text-slate-600">
        {children}
      </td>
    </tr>
  );
}

// ── Manual Entry Fields ───────────────────────────────────────────────────────

const MANUAL_FIELDS_CONFIG = [
  { key: "line6_gamingIncome",       line: "6",       label: "Net income — gaming & fundraising events" },
  { key: "line7_inventorySales",     line: "7",       label: "Gross sales of inventory" },
  { key: "line8_otherRevenue",       line: "8",       label: "Other revenue (attach schedule)" },
  { key: "line11_memberBenefits",    line: "11",      label: "Benefits paid to or for members" },
  { key: "line12_compensation",      line: "12",      label: "Salaries, other compensation & benefits" },
  { key: "line13_mgmtExpenses",      line: "13",      label: "Management & general expenses" },
  { key: "line15_affiliates",        line: "15",      label: "Payments to affiliates" },
  { key: "line16_otherExpenses",     line: "16",      label: "Other expenses (itemize on attachment)" },
  { key: "line19_priorNetAssets",    line: "19",      label: "Net assets at beginning of year (prior year ending)" },
] as const;

type ManualFieldKey = typeof MANUAL_FIELDS_CONFIG[number]["key"];
type ManualFields = Record<ManualFieldKey, string>;

const MANUAL_DEFAULT: ManualFields = {
  line6_gamingIncome: "",
  line7_inventorySales: "",
  line8_otherRevenue: "",
  line11_memberBenefits: "",
  line12_compensation: "",
  line13_mgmtExpenses: "",
  line15_affiliates: "",
  line16_otherExpenses: "",
  line19_priorNetAssets: "",
};

function loadManualFields(year: number): ManualFields {
  try {
    const stored = localStorage.getItem(`form990_manual_${year}`);
    return stored ? { ...MANUAL_DEFAULT, ...(JSON.parse(stored) as ManualFields) } : { ...MANUAL_DEFAULT };
  } catch {
    return { ...MANUAL_DEFAULT };
  }
}

// ── Main Component ────────────────────────────────────────────────────────────

export default function Form990() {
  const [, navigate] = useLocation();
  const qc = useQueryClient();
  const printRef = useRef<HTMLDivElement>(null);
  const [year, setYear] = useState(currentYear - 1); // Default to prior year
  const [filingNotes, setFilingNotes] = useState("");
  const [showMarkFiled, setShowMarkFiled] = useState(false);

  const [manualFields, setManualFieldsState] = useState<ManualFields>(() => loadManualFields(currentYear - 1));

  // Reload from localStorage when year changes
  useEffect(() => {
    setManualFieldsState(loadManualFields(year));
  }, [year]);

  function setManualField(key: ManualFieldKey, value: string) {
    setManualFieldsState((prev) => {
      const next = { ...prev, [key]: value };
      localStorage.setItem(`form990_manual_${year}`, JSON.stringify(next));
      return next;
    });
  }

  function fmtManual(key: ManualFieldKey): string {
    const v = parseFloat(manualFields[key]);
    return isNaN(v) ? "0.00" : v.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  }

  // ── Query ──────────────────────────────────────────────────────────────────

  const dataQ = useQuery({
    queryKey: ["form-990-data", year],
    queryFn: async () => {
      const r = await fetch(`api/admin/tax-filings/990?year=${year}`, {
        credentials: "include",
      });
      if (!r.ok) throw new Error(await r.text());
      return r.json() as Promise<Data990>;
    },
  });

  // ── Mark Filed Mutation ────────────────────────────────────────────────────

  const markFiledMut = useMutation({
    mutationFn: async () => {
      const r = await fetch("api/admin/tax-filings/990/mark-filed", {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ year, notes: filingNotes || undefined }),
      });
      if (!r.ok) throw new Error(await r.text());
      return r.json();
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["form-990-data", year] });
      setShowMarkFiled(false);
      setFilingNotes("");
    },
  });

  // ── Print ──────────────────────────────────────────────────────────────────

  function handlePrint() {
    window.print();
  }

  const d = dataQ.data;

  // ── Render ────────────────────────────────────────────────────────────────

  return (
    <div className="min-h-screen bg-background print:bg-white">
      {/* Nav — hidden in print */}
      <nav className="sticky top-0 z-50 border-b bg-slate-900 border-slate-700 print:hidden">
        <div className="max-w-5xl mx-auto px-4 h-14 flex items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <Button variant="ghost" size="sm" className="text-slate-300 hover:text-white hover:bg-slate-800" onClick={() => navigate("/admin")}>
              <ArrowLeft className="h-4 w-4 mr-1" /> Admin
            </Button>
            <Button variant="ghost" size="sm" className="text-slate-300 hover:text-white hover:bg-slate-800" onClick={() => navigate("/admin/tax-center")}>
              <FileText className="h-4 w-4 mr-1" /> 1099-C Center
            </Button>
            <span className="text-sm font-semibold text-white">Form 990-EZ Prep</span>
            <span className="hidden sm:inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-bold bg-red-600 text-white tracking-wide">ADMIN</span>
          </div>
          <div className="flex items-center gap-2">
            <Select value={String(year)} onValueChange={(v) => setYear(Number(v))}>
              <SelectTrigger className="w-28 h-8 text-xs">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {YEARS.map((y) => (
                  <SelectItem key={y} value={String(y)}>{y}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Button variant="outline" size="sm" onClick={handlePrint} className="gap-1">
              <Printer className="h-4 w-4" /> Print / PDF
            </Button>
            <UserButton />
          </div>
        </div>
      </nav>

      <div className="max-w-5xl mx-auto px-4 py-8 space-y-8 print:py-4 print:px-2">
        {/* Page Header */}
        <div className="print:hidden space-y-2">
          <h1 className="text-2xl font-bold tracking-tight">Form 990-EZ Preparation Tool</h1>
          <p className="text-sm text-muted-foreground">
            Auto-populated financial data for IRS Form 990-EZ (Short Form Return of Organization Exempt From Income Tax).
            Review all figures with your accountant before filing. Amounts shown are from your DebtBridge database for tax year {year}.
          </p>
        </div>

        {/* IRS Notice */}
        <div className="rounded-lg border border-amber-200 bg-amber-50 p-4 flex gap-3 print:hidden">
          <AlertTriangle className="h-5 w-5 text-amber-600 shrink-0 mt-0.5" />
          <div className="text-sm text-amber-900 space-y-1">
            <p className="font-semibold">Important: Review Before Filing</p>
            <p>
              This tool pre-populates known financial data from your database to assist in preparing Form 990-EZ.
              It is <strong>not</strong> a substitute for professional tax advice. Some fields (officer compensation,
              prior-year net assets, overhead expenses) require manual entry. File Form 990-EZ at{" "}
              <a href="https://www.irs.gov/charities-non-profits/annual-filing-and-forms" target="_blank" rel="noopener noreferrer" className="underline font-medium">
                IRS.gov
              </a>{" "}
              or through an e-file provider.
            </p>
          </div>
        </div>

        {dataQ.isLoading && (
          <div className="flex items-center gap-2 text-sm text-muted-foreground p-8">
            <RefreshCw className="h-4 w-4 animate-spin" />
            Loading financial data for {year}…
          </div>
        )}

        {dataQ.isError && (
          <div className="rounded-lg border border-red-200 bg-red-50 p-4 text-sm text-red-800">
            Failed to load data: {String(dataQ.error)}
          </div>
        )}

        {d && (
          <div ref={printRef} className="space-y-6">
            {/* Print Header */}
            <div className="hidden print:block text-center border-b pb-4 mb-6">
              <h1 className="text-xl font-bold">Form 990-EZ — Preparation Worksheet</h1>
              <p className="text-sm text-gray-600 mt-1">
                {d.organizationName} &nbsp;|&nbsp; EIN: {d.ein} &nbsp;|&nbsp; Tax Year: {d.taxYear}
              </p>
              <p className="text-xs text-gray-500 mt-1">
                Generated {new Date().toLocaleDateString("en-US", { dateStyle: "long" })} · For accountant review only — not the official IRS form
              </p>
            </div>

            {/* Organization Info */}
            <div className="rounded-lg border overflow-hidden">
              <div className="px-4 py-3 bg-slate-800 text-white flex items-center gap-2">
                <Building2 className="h-4 w-4" />
                <span className="text-sm font-semibold">Organization Information</span>
              </div>
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-4 p-4 text-sm">
                <div>
                  <p className="text-xs text-muted-foreground">Organization Name</p>
                  <p className="font-medium">{d.organizationName}</p>
                </div>
                <div>
                  <p className="text-xs text-muted-foreground">EIN</p>
                  <p className="font-mono font-medium">{d.ein}</p>
                </div>
                <div>
                  <p className="text-xs text-muted-foreground">Tax Year</p>
                  <p className="font-medium">{d.taxPeriodStart} to {d.taxPeriodEnd}</p>
                </div>
                <div>
                  <p className="text-xs text-muted-foreground">Form Type</p>
                  <p className="font-medium">990-EZ (Short Form)</p>
                </div>
                <div>
                  <p className="text-xs text-muted-foreground">Tax-Exempt Status</p>
                  <p className="font-medium">501(c)(3)</p>
                </div>
                <div>
                  <p className="text-xs text-muted-foreground">Accounting Method</p>
                  <p className="font-medium">Cash</p>
                </div>
              </div>
            </div>

            {/* Key Metrics */}
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 print:grid-cols-4">
              <div className="rounded-lg border p-4 space-y-1">
                <div className="flex items-center gap-1 text-xs text-muted-foreground">
                  <DollarSign className="h-3 w-3" /> Total Revenue
                </div>
                <p className="text-xl font-bold">{fmtD(d.partI.line9_totalRevenue)}</p>
              </div>
              <div className="rounded-lg border p-4 space-y-1">
                <div className="flex items-center gap-1 text-xs text-muted-foreground">
                  <DollarSign className="h-3 w-3" /> Total Expenses
                </div>
                <p className="text-xl font-bold">{fmtD(d.partII.line17_totalExpenses)}</p>
              </div>
              <div className="rounded-lg border p-4 space-y-1">
                <div className="flex items-center gap-1 text-xs text-muted-foreground">
                  <BarChart3 className="h-3 w-3" /> Net Assets
                </div>
                <p className="text-xl font-bold">{fmtD(d.totalNetAssets)}</p>
              </div>
              <div className="rounded-lg border p-4 space-y-1">
                <div className="flex items-center gap-1 text-xs text-muted-foreground">
                  <Users className="h-3 w-3" /> Beneficiaries
                </div>
                <p className="text-xl font-bold">{d.partIV.beneficiaryCount}</p>
              </div>
            </div>

            {/* Part I — Revenue */}
            <div className="rounded-lg border overflow-hidden">
              <table className="w-full">
                <thead>
                  <SectionHeader>Part I — Revenue, Expenses, and Changes in Net Assets</SectionHeader>
                </thead>
                <tbody>
                  <LineRow lineNum="1"  label="Contributions, gifts, grants, and similar amounts received" value={d.partI.line1_contributions} />
                  <LineRow lineNum="2"  label="Program service revenue" value={d.partI.line2_programServiceRevenue} indent />
                  <LineRow lineNum="3"  label="Membership dues and assessments" value={d.partI.line3_membershipDues} indent />
                  <LineRow lineNum="4"  label="Investment income" value={d.partI.line4_investmentIncome} indent />
                  <LineRow lineNum="5"  label="Other revenue" value={d.partI.line5_otherRevenue} indent />
                  <LineRow lineNum="9"  label="Total revenue (add lines 1–8)" value={d.partI.line9_totalRevenue} highlight />
                </tbody>
              </table>
            </div>

            {/* Part II — Expenses */}
            <div className="rounded-lg border overflow-hidden">
              <table className="w-full">
                <thead>
                  <SectionHeader>Part II — Expenses</SectionHeader>
                </thead>
                <tbody>
                  <LineRow lineNum="10" label="Grants and similar amounts paid (debt relief disbursements)" value={d.partII.line10_grantsPaid} />
                  <LineRow lineNum="13" label="Management and general expenses" value={d.partII.line13_managementExpenses} indent />
                  <LineRow lineNum="14" label="Fundraising expenses" value={d.partII.line14_fundraisingExpenses} indent />
                  <LineRow lineNum="17" label="Total expenses (add lines 10–16)" value={d.partII.line17_totalExpenses} highlight />
                  <LineRow
                    lineNum="18"
                    label={d.partII.line18_excessOrDeficit >= 0 ? "Excess of revenue over expenses" : "Deficit (expenses over revenue)"}
                    value={d.partII.line18_excessOrDeficit}
                    highlight
                  />
                </tbody>
              </table>
            </div>

            {/* Part III — Net Assets */}
            <div className="rounded-lg border overflow-hidden">
              <table className="w-full">
                <thead>
                  <SectionHeader>Part III — Balance Sheets (Net Assets / Fund Balances)</SectionHeader>
                </thead>
                <tbody>
                  <LineRow lineNum="19" label="Net assets or fund balances at beginning of year" value={d.partIII.line19_netAssetsBeginning} />
                  <LineRow lineNum="20" label="Other changes in net assets or fund balances" value={d.partIII.line20_otherChanges} indent />
                  <LineRow lineNum="21" label="Net assets or fund balances at end of year" value={d.partIII.line21_netAssetsEnd} highlight />
                </tbody>
              </table>
            </div>

            {/* Part IV — Program Service Accomplishments */}
            <div className="rounded-lg border overflow-hidden">
              <div className="px-4 py-3 bg-slate-100 border-b">
                <span className="text-xs font-bold uppercase tracking-wide text-slate-600">
                  Part IV — Program Service Accomplishments
                </span>
              </div>
              <div className="p-4 space-y-4">
                <div>
                  <p className="text-xs text-muted-foreground mb-1">Program Description (Line 28)</p>
                  <p className="text-sm leading-relaxed bg-muted/30 p-3 rounded border">{d.partIV.programDescription}</p>
                </div>
                <div className="grid grid-cols-3 gap-4 text-sm">
                  <div className="rounded border p-3 text-center">
                    <p className="text-2xl font-bold">{d.partIV.debtsClearedCount}</p>
                    <p className="text-xs text-muted-foreground mt-1">Debts Cleared</p>
                  </div>
                  <div className="rounded border p-3 text-center">
                    <p className="text-2xl font-bold">{d.partIV.beneficiaryCount}</p>
                    <p className="text-xs text-muted-foreground mt-1">Beneficiaries Served</p>
                  </div>
                  <div className="rounded border p-3 text-center">
                    <p className="text-2xl font-bold">{d.partIV.donorCount}</p>
                    <p className="text-xs text-muted-foreground mt-1">Donors</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Active Debt Relief Pools */}
            {d.pools.length > 0 && (
              <div className="rounded-lg border overflow-hidden">
                <div className="px-4 py-3 bg-slate-100 border-b">
                  <span className="text-xs font-bold uppercase tracking-wide text-slate-600">
                    Active Debt Relief Pools (Supporting Schedule)
                  </span>
                </div>
                <table className="w-full">
                  <thead>
                    <tr className="text-xs uppercase text-muted-foreground border-b">
                      <th className="py-2 px-4 text-left font-medium">Pool Name</th>
                      <th className="py-2 px-4 text-left font-medium">Status</th>
                      <th className="py-2 px-4 text-right font-medium">Balance</th>
                    </tr>
                  </thead>
                  <tbody>
                    {d.pools.map((p, i) => (
                      <tr key={i} className="border-b last:border-0">
                        <td className="py-2 px-4 text-sm">{p.name}</td>
                        <td className="py-2 px-4">
                          <Badge
                            variant="outline"
                            className={`text-xs ${p.status === "active" ? "bg-green-50 text-green-700 border-green-300" : "bg-gray-50 text-gray-600"}`}
                          >
                            {p.status}
                          </Badge>
                        </td>
                        <td className="py-2 px-4 text-right text-sm font-mono">{fmtD(p.balance)}</td>
                      </tr>
                    ))}
                    <tr className="font-semibold">
                      <td className="py-2 px-4 text-sm">Total Net Assets</td>
                      <td />
                      <td className="py-2 px-4 text-right text-sm font-mono">{fmtD(d.totalNetAssets)}</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            )}

            {/* Fields Requiring Manual Entry */}
            <div className="rounded-lg border border-dashed border-amber-300 p-4 space-y-3">
              <div>
                <h3 className="text-sm font-semibold text-amber-800 flex items-center gap-2">
                  <AlertTriangle className="h-4 w-4" />
                  Manual Entry Fields (Accountant Required)
                </h3>
                <p className="text-xs text-amber-700 mt-1">
                  Enter values below. They are saved locally per tax year and included in the print view for your accountant.
                </p>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                {MANUAL_FIELDS_CONFIG.map((field) => (
                  <div key={field.key} className="space-y-1">
                    <label className="text-xs font-medium text-amber-900 block">
                      Line {field.line}: {field.label}
                    </label>
                    <div className="flex items-center gap-1 print:hidden">
                      <span className="text-sm text-muted-foreground">$</span>
                      <input
                        type="number"
                        min="0"
                        step="0.01"
                        className="border rounded px-2 py-1 text-sm w-full font-mono bg-background focus:outline-none focus:ring-2 focus:ring-ring"
                        value={manualFields[field.key as ManualFieldKey]}
                        onChange={(e) => setManualField(field.key as ManualFieldKey, e.target.value)}
                        placeholder="0.00"
                      />
                    </div>
                    <div className="hidden print:block text-sm font-mono text-foreground">
                      ${fmtManual(field.key as ManualFieldKey)}
                    </div>
                  </div>
                ))}
              </div>

              <p className="text-xs text-amber-600 border-t border-amber-200 pt-2 mt-2">
                Additional items requiring accountant preparation: Part V (officers/directors), Part VI (other information), Schedule A (public support test), debtors' SSN/TIN for 1099-C.
              </p>
            </div>

            {/* Mark as Filed Section */}
            <div className="rounded-lg border p-4 space-y-3 print:hidden">
              <div className="flex items-center justify-between">
                <h3 className="text-sm font-semibold">Filing Status</h3>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setShowMarkFiled(!showMarkFiled)}
                  className="gap-1"
                >
                  <CheckCircle2 className="h-4 w-4" />
                  Mark 990 as Filed
                </Button>
              </div>
              {showMarkFiled && (
                <div className="space-y-3">
                  <Textarea
                    placeholder="Optional notes (e.g. filed via e-file provider, confirmation number, accountant name…)"
                    value={filingNotes}
                    onChange={(e) => setFilingNotes(e.target.value)}
                    rows={2}
                    className="text-sm"
                  />
                  <div className="flex gap-2">
                    <Button
                      size="sm"
                      onClick={() => markFiledMut.mutate()}
                      disabled={markFiledMut.isPending}
                      className="gap-1"
                    >
                      {markFiledMut.isPending ? <RefreshCw className="h-3.5 w-3.5 animate-spin" /> : <CheckCircle2 className="h-3.5 w-3.5" />}
                      Confirm Filed
                    </Button>
                    <Button size="sm" variant="ghost" onClick={() => setShowMarkFiled(false)}>
                      Cancel
                    </Button>
                  </div>
                  {markFiledMut.isSuccess && (
                    <p className="text-xs text-green-700 flex items-center gap-1">
                      <CheckCircle2 className="h-3.5 w-3.5" />
                      Form 990 marked as filed for tax year {year}.
                    </p>
                  )}
                  {markFiledMut.isError && (
                    <p className="text-xs text-red-700">Error: {String(markFiledMut.error)}</p>
                  )}
                </div>
              )}
            </div>

            {/* Print Footer */}
            <div className="hidden print:block text-xs text-gray-500 text-center pt-4 border-t mt-6">
              Prepared by DebtBridge Foundation admin system · {new Date().toLocaleDateString("en-US", { dateStyle: "long" })} ·
              This worksheet is for internal use only and is not the official IRS filing.
            </div>
          </div>
        )}
      </div>
      <QuoteCarousel />

    </div>
  );
}
